/**
 * Market cart: one per seller (an order is always one seller), kept in this browser. Prices are
 * shown from the snapshot; the server prices the order again when it is created.
 */
import { useCallback, useEffect, useState } from "react";

export interface MarketCartLine {
  listingId: number;
  quantity: number;
  max: number;
  name: string;
  priceArs: number;
  photo: string | null;
  /** Accepted offer: its price, for up to `quantity` cards. */
  offerId?: number | null;
}

type Carts = Record<string, MarketCartLine[]>;
const KEY = "pf-market-cart";
const EVENT = "pf-market-cart";

function read(): Carts {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) ?? "{}") as unknown;
    return parsed && typeof parsed === "object" ? (parsed as Carts) : {};
  } catch {
    return {};
  }
}

function write(carts: Carts): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(carts));
  } catch {
    // No storage (private mode): the cart lives until the page closes.
  }
  window.dispatchEvent(new Event(EVENT));
}

export function useMarketCart() {
  const [carts, setCarts] = useState<Carts>(read);
  useEffect(() => {
    const sync = () => setCarts(read());
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const add = useCallback((handle: string, line: MarketCartLine) => {
    const all = read();
    const lines = all[handle] ?? [];
    const existing = lines.find((l) => l.listingId === line.listingId);
    const next = existing
      ? lines.map((l) => (l.listingId === line.listingId ? { ...l, ...line, quantity: Math.min(line.max, line.offerId ? line.quantity : l.quantity + line.quantity) } : l))
      : [...lines, line];
    write({ ...all, [handle]: next });
  }, []);

  const setQuantity = useCallback((handle: string, listingId: number, quantity: number) => {
    const all = read();
    const lines = (all[handle] ?? []).map((l) => (l.listingId === listingId ? { ...l, quantity: Math.max(1, Math.min(l.max, quantity)) } : l));
    write({ ...all, [handle]: lines });
  }, []);

  /** The listings' prices now (the order said they changed): the cart shows what will be charged. */
  const setPrices = useCallback((handle: string, prices: { listingId: number; priceArs: number }[]) => {
    const all = read();
    const lines = (all[handle] ?? []).map((l) => {
      const p = prices.find((x) => x.listingId === l.listingId);
      return p && !l.offerId ? { ...l, priceArs: p.priceArs } : l;
    });
    write({ ...all, [handle]: lines });
  }, []);

  const remove = useCallback((handle: string, listingId: number) => {
    const all = read();
    const lines = (all[handle] ?? []).filter((l) => l.listingId !== listingId);
    const { [handle]: _drop, ...rest } = all;
    write(lines.length ? { ...rest, [handle]: lines } : rest);
  }, []);

  const clear = useCallback((handle: string) => {
    const { [handle]: _drop, ...rest } = read();
    write(rest);
  }, []);

  return { carts, add, setQuantity, setPrices, remove, clear };
}

const OPEN_EVENT = "pf-market-cart-open";

/** Opens the market cart drawer (MarketCartDrawer) from anywhere. */
export function openMarketCart(): void {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

/** Calls `fn` whenever something asks to open the market cart. */
export function onOpenMarketCart(fn: () => void): () => void {
  window.addEventListener(OPEN_EVENT, fn);
  return () => window.removeEventListener(OPEN_EVENT, fn);
}
